import { AgDialog, useSaladBar } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import callBackendApi, { useRedisWritesBlocked } from '@/apis/call';
import { Supporter, SupporterList } from '@/types';

const useReleaseSelectedContacts = (listId: number | undefined) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { enqueueSuccessNotification } = useSaladBar();
  return useMutation({
    mutationFn: async (contacts: Supporter[]) => {
      const token = await getAccessTokenSilently();
      if (listId === undefined) throw Error('Can only release contacts from a list with an id');
      await callBackendApi({
        method: 'put',
        token,
        endpoint: `/lists/${listId}/contacts`,
        data: { release: { contactIds: contacts.map((contact) => contact.id) } },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['list', listId] });
      enqueueSuccessNotification('Contact(s) have been released');
    },
  });
};

export default function ReleaseSelectedContactsDialog({
  isOpen,
  onClose,
  selectedContacts,
  setSelectedContacts,
  list,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedContacts: Supporter[];
  setSelectedContacts: (contacts: Supporter[]) => void;
  list: SupporterList | undefined;
}) {
  const { mutateAsync: releaseSelectedContacts } = useReleaseSelectedContacts(list?.id);
  const { enqueueErrorNotification } = useSaladBar();
  const writesBlocked = useRedisWritesBlocked();

  return (
    <AgDialog
      dialogTitle="Release selected contacts"
      isOpen={isOpen}
      onClose={onClose}
      primaryButton={{
        text: 'Release',
        disabled: writesBlocked,
        onClick: async () => {
          if (writesBlocked) return;
          try {
            await releaseSelectedContacts(selectedContacts);
            setSelectedContacts([]);
            onClose();
          } catch {
            enqueueErrorNotification('Contacts could not be released.');
          }
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column"
        }}>
        <Typography>
          Are you sure you want to release the selected people in the list{' '}
          <strong>{list?.name}</strong>?
        </Typography>
        <Typography variant="explainer">
          <em>Note: this will release contacts so that they can be reassigned to other callers.</em>
        </Typography>
      </Box>
    </AgDialog>
  );
}
